import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PURE_WIRELESS_CAPABILITY } from '@rsos/capstone/src/constants/capabilities';
import { GOOGLE, ESRI } from '@rsos/constants/mapProviders';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import useHasCapability from '@rsos/hooks/useHasCapability';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import usePortalType from '@rsos/utils/usePortalType';
import useGeoSelectionMapCursor from '../../../hooks/useGeoSelectionMapCursor';
import PureWirelessESRI from '../../JurisdictionView/Map/PureWireless/PureWirelessESRI';
import {
  trackToggleMarkupMode,
  trackUseCompassNavigation,
} from '../../JurisdictionView/Map/gainsight/gisTrackings';
import {
  mapMarkupSelector,
  saveAllGraphics,
} from '../../JurisdictionView/Map/mapMarkupSlice';
import { AxonGLNext } from './Axon/useAxonGLNext';
import CallsGL from './CallsGL/CallsGL';
import ClickToIdentifyGL from './ClickToIdentifyGL/ClickToIdentifyGL';
import ClickToIdentifyPopover from './ClickToIdentifyGL/ClickToIdentifyPopover';
import DynamicLayersGL from './DynamicLayersGL/DynamicLayersGL';
import { EagleEyeGL } from './EagleEye/useEagleEye';
import EsriSketchGL from './EsriSketchGL/EsriSketchGL';
import { GisSearchGL } from './GisSearchGL';
import JurisdictionGL from './JurisdictionGL';
import MapPopover from './MapPopover';
import AirAmbulancePopover from './MapPopover/AirAmbulancePopover';
import IncidentMapPopover from './MapPopover/IncidentMapPopover';
import NonCallMapPopover from './MapPopover/NonCallMapPopover';
import NonCallsGL from './NonCallsGL/NonCallsGL';
import PureWirelessGL from './PureWireless/PureWirelessGL';
import StaticLayersGL from './StaticLayersGL/StaticLayersGL';
import TempLocationGL from './TempLocationGL/TempLocationGL';
import layers from './storeLayers';
import useDeckOverlay from './useDeckOverlay';

/**
 * @typedef {Object} DeckGLNextProps
 * @property {string} [googleMapId=''] - Optional Google Map ID used to configure DeckGL overlay.
 * @property {Object} [map=null] - Map which overlays and layers are added.
 * @property {Object} map.mapInstance - The actual map instance.
 * @property {string} [removedID=''] - Removed alert or 911 event ID.
 * @property {string} [activeQueueTab=''] - Name of the active queue tab.
 * @property {boolean} [isMapIconDisabled=false] - Whether the oblique imagery tool is active.
 * @property {Object} [mapRef=null] - Reference to the map instance.
 */

/**
 * DeckGLNext Component
 * Integrates multiple DeckGL-based layers and functionalities into a map interface.
 * Dynamically manages layers, overlays, and map interactions based on the map provider.
 *
 * @param {DeckGLNextProps} props - Component props
 */
const DeckGLNext = React.memo(
  /**
   * This extra comment is needed for proper display of JSDoc in VSCode's editor for destructured props.
   * @param {DeckGLNextProps} props
   */
  ({
    googleMapId = '',
    map = null,
    removedID = '',
    activeQueueTab = '',
    isMapIconDisabled = false,
    mapRef = null,
  }) => {
    const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);
    const dispatch = useDispatch();
    const mapMarkup = useSelector(mapMarkupSelector);
    const handleSaveGraphics = useCallback(
      graphics => dispatch(saveAllGraphics(graphics)),
      [dispatch],
    );
    const { isPremiumUser } = usePortalType();
    const overlay = useDeckOverlay(mapProvider, googleMapId);
    const shapes = useSelector(
      state => state.psaps.currentPSAP?.jurisdictions?.[0]?.shapes ?? null,
    );
    const hasPureWirelessCapability = useHasCapability(
      PURE_WIRELESS_CAPABILITY,
    ).hasCapability;

    const hasEsriMapAccess =
      useSelector(state =>
        checkEsriMapAccess({
          psaps: state.psaps,
          sinatra: state.sinatra,
        }),
      ) && isPremiumUser;
    const hasAccessToAirAmbulance = useHasAccessToAirAmbulance();
    const isPureWirelessLayerEnabled = hasPureWirelessCapability;

    // Synchronizes the DeckGL overlay with the provided map instance.
    // - If both `map` and `overlay` are available, the overlay is assigned to the map instance.
    // - If `overlay` is unavailable, all deck.gl layers are removed by calling `layers.deleteAll()`.
    // Dependencies include `map` and `overlay` to ensure the synchronization occurs whenever the
    // map instance or overlay changes.
    useEffect(() => {
      if (map && overlay) {
        overlay.setMap(map.mapInstance);
      }

      if (!overlay) {
        layers.deleteAll();
      }
    }, [map, overlay]);

    // set map cursor based on isMapIconDisabled
    useGeoSelectionMapCursor({ map, mapProvider, overlay });

    return (
      <>
        <JurisdictionGL overlay={overlay} />
        {/* PureWireless uses deck.gl MVTLayer for both Google and ESRI maps */}
        {/* ESRI's native VectorTileLayer crashes with 911inside.com MVT tiles */}
        {isPureWirelessLayerEnabled && mapProvider === GOOGLE && (
          <PureWirelessGL overlay={overlay} map={map} />
        )}
        {isPureWirelessLayerEnabled && mapProvider === ESRI && (
          <PureWirelessESRI overlay={overlay} map={map} />
        )}
        <CallsGL
          map={map}
          overlay={overlay}
          activeQueueTab={activeQueueTab}
          isMapIconDisabled={isMapIconDisabled}
          boundingBox={shapes}
        />
        <MapPopover overlay={overlay} />

        {hasEsriMapAccess && (
          <>
            <ClickToIdentifyGL mapInstance={map.mapInstance} ref={mapRef} />
            <ClickToIdentifyPopover overlay={overlay} />
          </>
        )}
        <IncidentMapPopover overlay={overlay} />
        {hasAccessToAirAmbulance && (
          <>
            <NonCallMapPopover overlay={overlay} />
            <AirAmbulancePopover overlay={overlay} />
            <NonCallsGL
              map={map}
              overlay={overlay}
              isMapIconDisabled={isMapIconDisabled}
            />
          </>
        )}
        <AxonGLNext overlay={overlay} isMapIconDisabled={isMapIconDisabled} />
        <EagleEyeGL overlay={overlay} isMapIconDisabled={isMapIconDisabled} />
        <StaticLayersGL
          overlay={overlay}
          removedID={removedID}
          isMapIconDisabled={isMapIconDisabled}
        />
        <DynamicLayersGL
          overlay={overlay}
          isMapIconDisabled={isMapIconDisabled}
        />
        <GisSearchGL
          map={map}
          overlay={overlay}
          isMapIconDisabled={isMapIconDisabled}
        />
        <TempLocationGL
          map={map}
          overlay={overlay}
          isMapIconDisabled={isMapIconDisabled}
        />
        {mapProvider === ESRI && (
          <EsriSketchGL
            map={map}
            overlay={overlay}
            mapProvider={mapProvider}
            mapMarkup={mapMarkup}
            onSaveGraphics={handleSaveGraphics}
            onToggleMarkupMode={trackToggleMarkupMode}
            onTrackCompass={trackUseCompassNavigation}
          />
        )}
      </>
    );
  },
);

export default DeckGLNext;
